import {
  Box,
  Stack,
  Typography,
  MenuItem,
  Tooltip,
  Accordion,
  Button,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  Divider,
  Chip,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from 'src/auth/context/jwt';
import { RHFSelect, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import SvgColor from 'src/components/svg-color';
import { useJobCreationContext } from 'src/sections/job2/hooks/use-job-creation-context';
import { createQuestions } from 'src/services/role.service';
import { formatDate } from 'src/utils/formatDate';
import PropTypes from 'prop-types';
import CodingAssessment from './coding-assessment/index';
import InteractiveInterview from './interactive-interview';

const INTERVIEW_DEADLINES = Array.from({ length: 30 }, (_, index) => {
  const daysAhead = index + 1;
  const label = daysAhead === 1 ? 'Next 1 day' : `Next ${daysAhead} days`;
  return {
    label,
    value: daysAhead,
  };
});

export default function AIInterview({
  watch,
  errors,
  methods,
  setError,
  clearErrors,
  setValue,
  isAiInterviewExpanded,
  setIsAiInterviewExpanded,
  interviewQuestionFields,
  removeInterviewQuestion,
  appendInterviewQuestion,
  codingAssessmentFields,
  appendCodingAssessment,
  removeCodingAssessment,
}) {
  // ref
  const accordianRef = useRef(null);
  const aiInterviewDeadlineSelect = useRef(null);

  // contexts
  const context = useContext(AuthContext);

  // states
  const [isLoading, setIsLoading] = useState(false);
  const [isHoveredAIInterviewQuestion, setIsHoveredAIInterviewQuestion] =
    useState(null);
  const [showCodingAssessment, setShowCodingAssessment] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const showInteractiveInterview = watch('interactiveInterview.showInteractiveInterview');

  const {
    canEdit,
    mode,
    jobId,
    jobName,
    jobDetails,
    setInterviewQuestions,
    setJobDetails,
    clickedStage,
    setClickedStage,
    codingAssessments,
    needCodingAssessment,
  } = useJobCreationContext();

  const SECTION_CHIPS = [
    {
      label: 'Coding Assessment',
      value: 'codingAssessment',
      type: 'codingAssessment',
      show: codingAssessmentFields.length === 0,
      onClick: () => {
        appendCodingAssessment({
          questions: [
            {
              text: '',
              testCases: [],
              timeLimit: 30,
            },
          ],
          languages: [],
        });
      },
    },
    // {
    //   label: 'Interactive Interview',
    //   value: 'interactiveInterview',
    //   show: !showInteractiveInterview,
    //   type: 'interactiveInterview',
    //   onClick: () => {
    //     methods.setValue('interactiveInterview.showInteractiveInterview', true);
    //   },
    // },
  ];

  // functions
  const generateQuestions = () => {
    const watchNoOfQuestions = watch('noOfQuestions');

    if (watchNoOfQuestions === '') {
      setError('noOfQuestions', {
        message: 'Please enter numeric value',
      });
    } else {
      clearErrors('noOfQuestions');
      setIsLoading(true);
      const noOfQuestions = watch('noOfQuestions');

      const no_of_ques = parseInt(noOfQuestions, 10);
      const date = new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 45);

      const reqBody = {
        name: jobName,
        interview_type: 'STRUCTURED',
        duration: 5400,
        no_of_questions: parseInt(no_of_ques, 10),
        language: 'en',
        valid_till: formatDate(date),
      };

      createQuestions(context.user.organization_id, jobId, reqBody)
        .then(_data => {
          setJobDetails({
            ...jobDetails,
            questions: _data.body.questions,
          });
          setValue(
            'interviewQuestions',
            _data.body.questions
              ? _data.body.questions.map(question => ({
                  question: question.text,
                }))
              : [],
          );
          setInterviewQuestions(
            _data.body.questions
              ? _data.body.questions.map(question => ({
                  question: question.text,
                }))
              : [],
          );
        })
        .catch(err => {
          console.log(err);
          enqueueSnackbar({
            message: `Something went wrong`,
            variant: 'error',
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  // effects
  useEffect(() => {
    if (codingAssessments.length) {
      setShowCodingAssessment(true);
    } else {
      setShowCodingAssessment(false);
    }
  }, [codingAssessments]);

  return (
    <Accordion
      ref={accordianRef}
      onChange={() => {
        if (clickedStage === 'AI_INTERVIEW') {
          setClickedStage('CONTINUE');
        }
      }}
      sx={{
        width: '100%',
      }}
      expanded={isAiInterviewExpanded}
    >
      <AccordionSummary>
        <Stack spacing="24px" sx={{ width: '100%' }}>
          <Stack spacing="12px">
            <Typography variant="h3">AI Interview</Typography>
            <Typography
              variant="input"
              color={theme => theme.palette.content.primaryBlack3}
            >
              Utilize AI-generated questions to assess candidates&apos; skills.
              E.g., In your previous position as a growth hacker, what campaign
              did you enjoy working on?
            </Typography>
          </Stack>
          {!isAiInterviewExpanded && (
            <Button
              fullWidth
              variant={
                mode !== 'EDIT' && clickedStage === 'AI_INTERVIEW'
                  ? 'contained'
                  : 'soft'
              }
              onClick={() => {
                setIsAiInterviewExpanded(!isAiInterviewExpanded);
              }}
              endIcon={<Iconify icon="tabler:chevron-down" />}
            >
              Custom Setup
            </Button>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={4}>
          <Stack spacing={2}>
            {!interviewQuestionFields.length && (
              <Stack spacing={2}>
                <Typography variant="subheading2">
                  How many interview questions do you want to ask?
                </Typography>
                <RHFTextField
                  name="noOfQuestions"
                  placeholder="Enter no. of questions"
                  sx={{ width: '40%' }}
                  disabled={!canEdit}
                />
                <Stack spacing="8px">
                  <LoadingButton
                    id="generate-questions-button"
                    variant="contained"
                    loading={isLoading}
                    loadingPosition="start"
                    sx={{
                      width: 'min-content',
                      whiteSpace: 'nowrap',
                      color: theme => theme.palette.content.primaryBlack1,
                      background: theme =>
                        theme.palette.background.primaryGreen3,
                      '&:hover': {
                        color: theme => theme.palette.content.primaryWhite,
                        background: theme =>
                          theme.palette.background.primaryBlack1,
                      },
                    }}
                    startIcon={
                      <SvgColor src="/assets/icons/job/ic_star_twinkle.svg" />
                    }
                    onClick={generateQuestions}
                    disabled={!canEdit}
                  >
                    {isLoading ? 'Generating with AI' : 'Generate with AI'}
                  </LoadingButton>
                  {errors?.interviewQuestions && (
                    <Typography variant="caption" px="6px" color="error.main">
                      Please generate interview questions
                    </Typography>
                  )}
                </Stack>
              </Stack>
            )}
            <Stack spacing={3}>
              {interviewQuestionFields.map((item, index) => {
                const questionFieldName = `interviewQuestions.${index}.question`;
                return (
                  <Stack
                    key={item.id}
                    spacing="8.5px"
                    onMouseEnter={() => setIsHoveredAIInterviewQuestion(index)}
                    onMouseLeave={() => setIsHoveredAIInterviewQuestion(null)}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="input" fontWeight={500}>
                        Question {index + 1}
                      </Typography>
                      {canEdit && (
                        <Tooltip
                          arrow
                          title={
                            interviewQuestionFields.length > 1
                              ? 'Delete'
                              : 'Atleast 1 question'
                          }
                        >
                          <Box
                            sx={{
                              width: '28px',
                              height: '28px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '4px',
                              padding: '2px',
                              border: theme =>
                                `0.5px solid ${theme.palette.content.primaryBlack3}`,
                              alignSelf: 'flex-end',
                              cursor:
                                interviewQuestionFields.length === 1
                                  ? 'default'
                                  : 'pointer',
                              transition: 'all 0.3s ease-in-out',
                              visibility:
                                isHoveredAIInterviewQuestion === index &&
                                interviewQuestionFields.length !== 1
                                  ? 'visible'
                                  : 'hidden',
                            }}
                            onClick={() => {
                              if (canEdit) {
                                if (interviewQuestionFields.length !== 1)
                                  removeInterviewQuestion(index);
                              }
                            }}
                          >
                            <Iconify
                              sx={{
                                color: theme =>
                                  theme.palette.content.primaryBlack3,
                              }}
                              icon="tabler:trash"
                            />
                          </Box>
                        </Tooltip>
                      )}
                    </Stack>
                    <RHFTextField
                      name={questionFieldName}
                      multiline
                      disabled={!canEdit}
                    />
                  </Stack>
                );
              })}
            </Stack>
            <Button
              startIcon={<Iconify icon="material-symbols:add" />}
              sx={{
                width: 'max-content',
                whiteSpace: 'nowrap',
                display: !interviewQuestionFields.length && 'none',
              }}
              variant="outlined"
              size="small"
              disabled={!canEdit}
              onClick={() => appendInterviewQuestion({ question: '' })}
            >
              Add Question
            </Button>
          </Stack>

          <Stack spacing="20px">
            {needCodingAssessment ? (
              <>
                <Divider />

                {showCodingAssessment
                  ? codingAssessmentFields.map((field, index) => (
                      <CodingAssessment
                        key={index}
                        index={index}
                        methods={methods}
                        removeCodingAssessment={removeCodingAssessment}
                      />
                    ))
                  : null}
              </>
            ) : null}

            {showInteractiveInterview && (
              <>
                <Divider />
                <InteractiveInterview methods={methods}/>
              </>
            )}

            {SECTION_CHIPS.some(section => section.show) && (
              <>
                <Divider />
                <Stack spacing="12px">
                  <Typography variant="subheading1" fontSize={16}>
                    Add section
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    flexWrap="wrap"
                  >
                    {SECTION_CHIPS.map(section => {
                      if (!section.show) return null;

                      return (
                        <Chip
                          key={section.value}
                          icon={
                            section.value === 'custom' && (
                              <Iconify icon="tabler:plus" />
                            )
                          }
                          disabled={!canEdit}
                          color="info"
                          variant="soft"
                          label={section.label}
                          onClick={section.onClick}
                        />
                      );
                    })}
                  </Stack>
                </Stack>
              </>
            )}

            {interviewQuestionFields.length ? (
              <>
                <Divider />
                <Stack spacing={2}>
                  <Typography variant="subheading1">
                    AI Interview Deadline
                  </Typography>
                  <RHFSelect
                    ref={aiInterviewDeadlineSelect}
                    name="aiInterviewDeadline"
                    disabled={!canEdit}
                  >
                    {INTERVIEW_DEADLINES.map(deadline => (
                      <MenuItem key={deadline.value} value={deadline.value}>
                        {deadline.label}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                </Stack>
              </>
            ) : null}
          </Stack>
        </Stack>
      </AccordionDetails>
      <AccordionActions>
        {isAiInterviewExpanded && (
          <LoadingButton
            fullWidth
            variant="soft"
            onClick={() => setIsAiInterviewExpanded(!isAiInterviewExpanded)}
            endIcon={<Iconify icon="tabler:chevron-up" />}
          >
            View less
          </LoadingButton>
        )}
      </AccordionActions>
    </Accordion>
  );
}

AIInterview.propTypes = {
  watch: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  methods: PropTypes.object.isRequired,
  setError: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  isAiInterviewExpanded: PropTypes.bool.isRequired,
  setIsAiInterviewExpanded: PropTypes.func.isRequired,
  interviewQuestionFields: PropTypes.array.isRequired,
  removeInterviewQuestion: PropTypes.func.isRequired,
  appendInterviewQuestion: PropTypes.func.isRequired,
  codingAssessmentFields: PropTypes.array.isRequired,
  appendCodingAssessment: PropTypes.func.isRequired,
  removeCodingAssessment: PropTypes.func.isRequired,
};
